import { useState, useCallback, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { SourceType } from '@/types/source';
import ViinexService from '@/services/viinex';
import { buildVodUrl } from '@/utils/camera-stream';
import { useDebounce } from '@/hooks';
import {
    StreamingMode,
    StreamingStatus,
    UseStreamingModeOptions,
} from '@/types/streaming';
import {
    STREAMING_TIMING,
    STREAMING_ERRORS,
    MILLISECONDS_TO_SECONDS,
} from '@/constants/time';

const NOOP = () => {};

export function useStreamingMode({
    source,
    startTime,
    initialMode = StreamingMode.VOD,
}: UseStreamingModeOptions) {
    const isCamera = source?.type === SourceType.CAMERA;
    const sourceId = source?.id || '';

    const [mode, setMode] = useState<StreamingMode>(
        initialMode === StreamingMode.LIVE
            ? StreamingMode.LIVE
            : StreamingMode.VOD,
    );

    const liveStatus = useSWR(
        isCamera ? ['camera-live', sourceId] : null,
        () => ViinexService.getCameraLiveStatus(sourceId),
        {
            refreshInterval: STREAMING_TIMING.LIVE_STATUS_POLL_MS,
            revalidateOnFocus: false,
            shouldRetryOnError: false,
        },
    );
    const canGoLive = liveStatus.data?.live ?? false;

    const storageData = useSWR(
        isCamera ? ['camera-storage', sourceId] : null,
        () => ViinexService.fetchStorageData(sourceId),
        {
            refreshInterval: STREAMING_TIMING.ARCHIVE_REFRESH_MS,
            onErrorRetry: (
                _error,
                _key,
                _config,
                revalidate,
                { retryCount },
            ) => {
                setTimeout(
                    () => revalidate({ retryCount }),
                    STREAMING_TIMING.ARCHIVE_REFRESH_MS,
                );
            },
            revalidateOnFocus: false,
            shouldRetryOnError: true,
        },
    );

    const [targetTimestamp, setTargetTimestamp] = useState<number | undefined>(
        startTime,
    );

    const [hlsUrl, setHlsUrl] = useState<string | null>(null);
    const [streamBeginTimestamp, setStreamBeginTimestamp] = useState<
        number | null
    >(null);
    const [status, setStatus] = useState<StreamingStatus>(StreamingStatus.IDLE);
    const [error, setError] = useState<string | null>(null);

    const liveStatusResolved =
        liveStatus.data !== undefined || liveStatus.error != null;
    const live =
        mode === StreamingMode.LIVE && (canGoLive || !liveStatusResolved);
    const hasArchive = storageData.data != null;

    const timelineIntervals = useMemo(() => {
        return storageData.data?.intervals ?? [];
    }, [storageData.data]);

    useEffect(() => {
        setTargetTimestamp(startTime);
    }, [startTime]);

    useEffect(() => {
        if (!isCamera) return;
        if (initialMode === StreamingMode.LIVE) {
            setMode(StreamingMode.LIVE);
            setTargetTimestamp(undefined);
            return;
        }
        setMode(StreamingMode.VOD);
    }, [initialMode, isCamera]);

    useEffect(() => {
        if (!isCamera) return;
        if (mode === StreamingMode.LIVE && liveStatusResolved && !canGoLive) {
            setMode(StreamingMode.VOD);
        }
    }, [isCamera, mode, liveStatusResolved, canGoLive]);

    const timeline = useMemo(() => {
        if (timelineIntervals.length === 0) return null;

        const earliest = Math.min(
            ...timelineIntervals.map((interval) => interval.begin),
        );
        const latest = Math.max(
            ...timelineIntervals.map((interval) => interval.end),
        );

        return {
            intervals: timelineIntervals,
            earliest,
            latest,
            totalDuration: latest - earliest,
        };
    }, [timelineIntervals]);

    const shouldDebounce = isCamera && mode === StreamingMode.VOD;
    const debouncedTargetTimestamp = useDebounce(
        targetTimestamp,
        shouldDebounce ? 50 : 0,
    );

    useEffect(() => {
        if (!isCamera) return;

        if (mode === StreamingMode.LIVE) {
            setHlsUrl(null);
            setStreamBeginTimestamp(null);
            setStatus(StreamingStatus.READY);
            return;
        }

        if (targetTimestamp !== debouncedTargetTimestamp) return;

        let stale = false;
        buildVodUrl(sourceId, debouncedTargetTimestamp, {
            setHlsUrl: (url) => {
                if (!stale) setHlsUrl(url);
            },
            setStreamBeginTimestamp: (begin) => {
                if (!stale) setStreamBeginTimestamp(begin);
            },
            setStatus: (nextStatus) => {
                if (!stale) setStatus(nextStatus);
            },
            setError: (message) => {
                if (!stale) setError(message);
            },
        });
        return () => {
            stale = true;
        };
    }, [isCamera, mode, sourceId, targetTimestamp, debouncedTargetTimestamp]);

    const onGoLive = useCallback(() => {
        if (canGoLive) {
            setMode(StreamingMode.LIVE);
            setTargetTimestamp(undefined);
        }
    }, [canGoLive]);

    const onExitLive = useCallback(() => {
        setMode(StreamingMode.VOD);
        const targetTimestamp = timeline?.latest
            ? timeline.latest - STREAMING_TIMING.VOD_OFFSET_FROM_LIVE_SEC
            : Date.now() / MILLISECONDS_TO_SECONDS -
              STREAMING_TIMING.FALLBACK_SEEK_OFFSET_SEC;
        setTargetTimestamp(targetTimestamp);
    }, [timeline]);

    const onSeek = useCallback(
        (relativeTime: number) => {
            setMode(StreamingMode.VOD);
            const absoluteTimestamp =
                streamBeginTimestamp !== null
                    ? streamBeginTimestamp + relativeTime
                    : Date.now() / MILLISECONDS_TO_SECONDS -
                      STREAMING_TIMING.FALLBACK_SEEK_OFFSET_SEC +
                      relativeTime;
            setTargetTimestamp(absoluteTimestamp);
        },
        [streamBeginTimestamp],
    );

    const onSeekToTimestamp = useCallback((absoluteTimestamp: number) => {
        setTargetTimestamp(absoluteTimestamp);
        setMode(StreamingMode.VOD);
        return absoluteTimestamp;
    }, []);

    if (!isCamera) {
        return {
            shouldUseWebRTC: false,
            videoSrc: source?.src || null,
            live: false,
            canGoLive: false,
            hasArchive: true,
            status: StreamingStatus.READY,
            error: null,
            onGoLive: NOOP,
            onExitLive: NOOP,
            onSeek: NOOP,
            onSeekToTimestamp: NOOP,
            streamBeginTimestamp: null,
            targetTimestamp: undefined,
            timeline: null,
            storageService: null,
        };
    }

    return {
        shouldUseWebRTC: live,
        videoSrc: live ? null : hlsUrl,
        live,
        canGoLive,
        hasArchive,
        status,
        error:
            error ||
            (liveStatus.error ? STREAMING_ERRORS.LIVE_STATUS_FAILED : null),
        onGoLive,
        onExitLive,
        onSeek,
        onSeekToTimestamp,
        streamBeginTimestamp,
        targetTimestamp,
        timeline,
        storageService: storageData.data?.storageService ?? null,
    };
}
